import { dispatchSignals } from "@prelude.so/js-sdk/signals";
import { signInWithCustomToken } from "firebase/auth";
import {
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import { useOptionalParticipationFlow } from "~/components/participation/ParticipationFlow";
import { Text } from "~/components/Text";
import { Toaster } from "~/components/Toaster";

import { useClientAuth } from "~/hooks/use-auth";
import { useRootEnvironment, useRootLocale } from "~/root";
import { getRegionalCopy } from "~/utils/localization";
import type { CheckoutAuthResult } from "~/routes/api.events.$eventId.checkout-auth";
import { CheckoutPhoneNumberField } from "./CheckoutPhoneNumberField";
import { CheckoutQuestionStep } from "./CheckoutQuestionStep";
import {
  CHECKOUT_VERIFICATION_CODE_LENGTH,
  CheckoutVerificationCodeField,
} from "./CheckoutVerificationCodeField";

export type PhoneAuthenticationDraft = {
  phone: string;
  phoneInput: string;
  phoneCountry: string;
  verificationSentAt: number;
};

type Props = {
  eventId: string;
  draft: PhoneAuthenticationDraft;
  update: (patch: Partial<PhoneAuthenticationDraft>) => void;
  onComplete: () => void | Promise<void>;
  onBack: () => void;
  canContinue?: () => boolean;
  title?: string;
  description?: string;
  compact?: boolean;
};

export function PhoneAuthentication({
  eventId,
  draft,
  update,
  onComplete,
  onBack,
  canContinue = () => true,
  title,
  description = "Let’s get you in! We’ll text you a quick code",
  compact = false,
}: Props) {
  const flow = useOptionalParticipationFlow();
  const environment = useRootEnvironment();
  const regionalCopy = getRegionalCopy(useRootLocale());
  const auth = useClientAuth();

  const [stage, setStage] = useState<"phone" | "code">(
    draft.verificationSentAt > Date.now() - 10 * 60 * 1000 ? "code" : "phone",
  );
  const phone = draft.phone;
  const setPhone = (phone: string) => update({ phone, verificationSentAt: 0 });
  const [code, setCode] = useState("");
  const [phoneError, setPhoneError] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const verificationInFlight = useRef(false);
  const resendAt = draft.verificationSentAt + 60_000;
  const [resendRemaining, setResendRemaining] = useState(() =>
    Math.max(0, Math.ceil((resendAt - Date.now()) / 1000)),
  );
  const toasterId = useId();
  const toastId = useRef<string | number | undefined>(undefined);

  useEffect(
    () => () => {
      if (toastId.current !== undefined) toast.dismiss(toastId.current);
    },
    [stage],
  );

  useEffect(() => {
    const update = () =>
      setResendRemaining(
        Math.max(0, Math.ceil((resendAt - Date.now()) / 1000)),
      );
    update();
    if (stage !== "code") return;
    const timer = window.setInterval(update, 1000);
    document.addEventListener("visibilitychange", update);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", update);
    };
  }, [resendAt, stage]);

  const formattedPhone =
    parsePhoneNumberFromString(phone)?.formatInternational() ?? phone;

  const sendVerificationCode = async () => {
    const sdkKey = environment?.PRELUDE_SDK_KEY;
    if (!sdkKey) throw new Error("Phone authentication is not configured.");

    let dispatchID: string;
    try {
      dispatchID = await dispatchSignals(sdkKey);
    } catch {
      throw new Error("We couldn't send a code. Please try again.");
    }

    const formData = new FormData();
    formData.append("intent", "send-phone-verification-code");
    formData.append("phoneNumber", phone);
    formData.append("dispatchID", dispatchID);
    const response = await fetch(`/api/events/${eventId}/checkout-auth`, {
      method: "POST",
      body: formData,
    });
    const result = (await response.json()) as CheckoutAuthResult;
    if (!result.ok) {
      throw new Error(
        result.error ?? "We couldn't send a code. Please try again.",
      );
    }
  };

  const start = async () => {
    if (!isValidPhoneNumber(phone)) {
      setPhoneError(regionalCopy.invalidPhoneNumber);
      return false;
    }

    setLoading(true);
    setError(null);
    setPhoneError(undefined);
    try {
      await sendVerificationCode();
      update({ verificationSentAt: Date.now() });
      setStage("code");
      return true;
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "We couldn't send a code. Please try again.",
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (loading || resending || Date.now() < resendAt) return;
    setResending(true);
    setError(null);
    try {
      await sendVerificationCode();
      update({ verificationSentAt: Date.now() });
      setCode("");
      if (canContinue()) {
        if (toastId.current !== undefined) toast.dismiss(toastId.current);
        toastId.current = toast.success("Verification code resent", {
          toasterId,
        });
      }
    } catch (reason) {
      if (toastId.current !== undefined) toast.dismiss(toastId.current);
      setError(
        reason instanceof Error
          ? reason.message
          : "We couldn't resend the code. Please try again.",
      );
    } finally {
      setResending(false);
    }
  };

  const verifyCode = async (verificationCode = code) => {
    if (
      verificationInFlight.current ||
      loading ||
      resending ||
      verificationCode.length !== CHECKOUT_VERIFICATION_CODE_LENGTH
    )
      return false;
    if (!auth) {
      setError(
        "Phone authentication is not ready. Please refresh and try again.",
      );
      return false;
    }

    if (!canContinue()) return false;
    verificationInFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      const verification = new FormData();
      verification.append("intent", "verify-phone-verification-code");
      verification.append("phoneNumber", phone);
      verification.append("code", verificationCode.trim());
      const verificationResponse = await fetch(
        `/api/events/${eventId}/checkout-auth`,
        { method: "POST", body: verification },
      );
      const verificationResult =
        (await verificationResponse.json()) as CheckoutAuthResult;
      if (!verificationResult.ok || !verificationResult.token) {
        throw new Error(
          verificationResult.error ?? "Phone verification failed.",
        );
      }

      if (!canContinue()) return false;
      const credentials = await signInWithCustomToken(
        auth,
        verificationResult.token,
      );
      const idToken = await credentials.user.getIdToken();
      if (!canContinue()) return false;
      const session = new FormData();
      session.append("intent", "sign-in");
      session.append("idToken", idToken);
      const sessionResponse = await fetch(
        `/api/events/${eventId}/checkout-auth`,
        { method: "POST", body: session },
      );
      const sessionResult =
        (await sessionResponse.json()) as CheckoutAuthResult;
      if (!sessionResult.ok) {
        throw new Error(sessionResult.error ?? "Session setup failed.");
      }
      if (canContinue()) await onComplete();
      return true;
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Phone verification failed.",
      );
      return false;
    } finally {
      verificationInFlight.current = false;
      setLoading(false);
    }
  };

  const verify = (verificationCode = code) => flow
    ? flow.run("verify_code", () => verifyCode(verificationCode))
    : verifyCode(verificationCode);

  if (stage === "phone") {
    return (
      <CheckoutQuestionStep
        title={title ?? regionalCopy.phoneNumberQuestion}
        description={description}
        compact={compact}
        operation="send_code"
        onSubmit={start}
        onBack={onBack}
        loading={loading}
        submitLabel="Send code"
        error={error}
      >
        <CheckoutPhoneNumberField
          savedInput={draft.phoneInput}
          savedCountry={draft.phoneCountry}
          onInputChange={(phoneInput, phoneCountry) =>
            update({ phoneInput, phoneCountry })
          }
          value={phone}
          disabled={loading}
          error={phoneError}
          onChange={(value) => {
            setPhone(value);
            setPhoneError(undefined);
            setError(null);
          }}
        />
      </CheckoutQuestionStep>
    );
  }

  return (
    <CheckoutQuestionStep
      title="Enter your code"
      compact={compact}
      description={`We sent a ${CHECKOUT_VERIFICATION_CODE_LENGTH}-digit code to ${formattedPhone}.`}
      onSubmit={verify}
      onBack={() => {
        setStage("phone");
        setCode("");
        setError(null);
        update({ verificationSentAt: 0 });
      }}
      backDisabled={resending}
      loading={loading}
      submitDisabled={
        resending || code.length < CHECKOUT_VERIFICATION_CODE_LENGTH
      }
      submitLabel="Next"
      error={error}
    >
      <CheckoutVerificationCodeField
        value={code}
        invalid={Boolean(error)}
        disabled={loading || resending}
        onChange={(value) => {
          setCode(value);
          setError(null);
          if (
            value.length === CHECKOUT_VERIFICATION_CODE_LENGTH &&
            value !== code
          ) {
            void verify(value);
          }
        }}
      />
      <div className="mt-2 flex flex-wrap items-center gap-x-2 font-sans text-sm text-butter-green">
        <button
          type="button"
          className="min-h-11 font-normal underline underline-offset-4 transition-opacity hover:opacity-70 disabled:opacity-50"
          onClick={resend}
          disabled={loading || resending || resendRemaining > 0}
        >
          {resending
            ? "Resending…"
            : resendRemaining > 0
              ? `Resend code (${resendRemaining}s)`
              : "Resend code"}
        </button>
        <span aria-hidden="true">·</span>
        <button
          type="button"
          className="min-h-11 font-normal underline underline-offset-4 transition-opacity hover:opacity-70 disabled:opacity-50"
          disabled={loading || resending}
          onClick={() => {
            setStage("phone");
            setCode("");
            update({ verificationSentAt: 0 });
          }}
        >
          Change number
        </button>
      </div>
      <Text size="xs" className="mt-3 text-[0.625rem]! leading-relaxed opacity-80">
        By submitting your code, you consent to receive SMS messages from Butter at the
        number provided. Message frequency varies. Message and data rates may apply.
        Text HELP for help, STOP to cancel.{" "}
        <a
          href="https://joinbutter.co/terms-conditions"
          target="_blank"
          rel="noreferrer"
          className="underline focus-visible:decoration-2 focus-visible:underline-offset-4"
        >
          Terms &amp; Conditions
        </a>{" "}
        and{" "}
        <a
          href="https://joinbutter.co/privacy-policy"
          target="_blank"
          rel="noreferrer"
          className="underline focus-visible:decoration-2 focus-visible:underline-offset-4"
        >
          Privacy Policy
        </a>
        .
      </Text>
      {/* Keep announcements inside the modal's accessible subtree. */}
      <Toaster id={toasterId} />
    </CheckoutQuestionStep>
  );
}
